import { useEffect, useRef, useState, type ChangeEvent, type FocusEvent } from 'react';
import { parseRM } from '@bazaar/shared';
import clsx from 'clsx';

export interface MoneyInputProps {
  id: string;
  label: string;
  valueCents: number;
  /** Called with the parsed integer cents, or `null` while the current text is not a valid amount. */
  onChangeCents: (cents: number | null) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  error?: string;
  className?: string;
}

export function MoneyInput({
  id,
  label,
  valueCents,
  onChangeCents,
  min = 0,
  max,
  disabled,
  error,
  className,
}: MoneyInputProps) {
  const [text, setText] = useState(() => (valueCents / 100).toFixed(2));
  // Tracked in a ref, not state: a state flip on blur would itself be a
  // dependency change that reruns the sync effect below and could clobber
  // the text `handleBlur` just normalised (e.g. re-filling a field the user
  // just cleared with the last-known valueCents). Only an actual change to
  // `valueCents` should ever trigger a resync.
  const focusedRef = useRef(false);
  // The cents value this component itself last emitted via onChangeCents.
  // The parent typically just echoes that straight back as the next
  // `valueCents` prop - when it does, it's not an "external" change and
  // must not clobber whatever the user is mid-typing.
  const lastEmittedRef = useRef<number | null>(valueCents);

  useEffect(() => {
    // Never rewrite the box out from under an actively-typing user, and
    // don't reformat a value change that's just an echo of what this
    // component itself last reported.
    if (focusedRef.current) return;
    if (valueCents === lastEmittedRef.current) return;
    setText((valueCents / 100).toFixed(2));
    lastEmittedRef.current = valueCents;
  }, [valueCents]);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setText(raw);

    const cents = parseRM(raw);
    if (cents === null || cents < min || (max !== undefined && cents > max)) {
      lastEmittedRef.current = null;
      onChangeCents(null);
      return;
    }
    lastEmittedRef.current = cents;
    onChangeCents(cents);
  }

  function handleFocus(e: FocusEvent<HTMLInputElement>) {
    focusedRef.current = true;
    e.currentTarget.select();
  }

  function handleBlur() {
    focusedRef.current = false;
    const cents = parseRM(text);
    if (cents === null) return;
    setText((cents / 100).toFixed(2));
  }

  return (
    <div className={className}>
      <label htmlFor={id} className="block font-body font-semibold text-base text-teal-dark mb-1">
        {label}
      </label>
      <div className="relative">
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted font-heading font-semibold text-sm"
          aria-hidden="true"
        >
          RM
        </span>
        <input
          id={id}
          type="text"
          inputMode="decimal"
          value={text}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={clsx(
            'w-full min-h-12 rounded-lg border-2 pl-10 pr-3 text-lg font-heading',
            error ? 'border-danger' : 'border-border',
          )}
        />
      </div>
      {error && (
        <p id={`${id}-error`} role="alert" className="mt-1 font-body text-sm text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
